import React, { useState, useEffect } from 'react';
import { getOrders, updateOrderStatus, deleteOrder } from '../api/api';
import Comments from '../components/Comments';
import './AdminOrders.css';

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await getOrders({});
      setOrders(response.data.orders || response.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const notes = prompt(`Optional: Enter notes for status change to "${newStatus}"`);
    
    try {
      await updateOrderStatus(id, { 
        status: newStatus, 
        notes: notes || '' 
      });
      alert('Order status updated successfully');
      fetchOrders();
    } catch (error) {
      alert('Error updating status: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleDeleteOrder = async (order) => {
    if (order.status !== 'delivered' && order.status !== 'cancelled') {
      alert(`Cannot delete order. Order must be marked as 'delivered' or 'cancelled' before deletion.\n\nCurrent status: ${order.status}\n\nPlease update the order status first.`);
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete order ${order.orderNumber}?\n\n` +
      `Customer: ${order.customer.name}\n` +
      `Total: R${order.total}\n` +
      `Status: ${order.status}\n\n` +
      `This will download an Excel file with the order details and permanently remove it from the system.`
    );

    if (!confirmed) return;

    try {
      const response = await deleteOrder(order._id);
      
      // Download the Excel file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deleted-order-${order.orderNumber}-${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Order deleted successfully. Excel file has been downloaded.');
      fetchOrders();
    } catch (error) {
      alert('Error deleting order: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="admin-orders">
      <h1>Manage Orders</h1>
      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Order #</th>
              <th>Customer</th>
              <th>Total</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <React.Fragment key={order._id}>
                <tr>
                  <td><strong>{order.orderNumber}</strong></td>
                  <td>
                    {order.customer.name}<br/>
                    <small>{order.customer.email}</small>
                  </td>
                  <td><strong>R{order.total.toFixed(2)}</strong></td>
                  <td>
                    <span className={`status-badge status-${order.status}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td className="actions-cell">
                    <select 
                      value={order.status} 
                      onChange={(e) => handleStatusChange(order._id, e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button
                      onClick={() => setExpandedId(expandedId === order._id ? null : order._id)}
                      className="btn-notes"
                      title="View or add notes"
                    >
                      📝 Notes
                    </button>
                    <button
                      onClick={() => handleDeleteOrder(order)}
                      className="btn-delete"
                      title={order.status !== 'delivered' && order.status !== 'cancelled' 
                        ? 'Order must be delivered or cancelled before deletion' 
                        : 'Delete order and download Excel'}
                      disabled={order.status !== 'delivered' && order.status !== 'cancelled'}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
                {expandedId === order._id && (
                  <tr className="expanded-row">
                    <td colSpan="6">
                      <div className="expanded-content">
                        <Comments entityType="order" entityId={order._id} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminOrders;
